export type DealPrePopulation = {
  projectId: string;
  projectName: string;
  unitTypeKey: string | null;
  unitTypeLabel: string | null;
  unitPrice: number | null;
  currency: string;
  // "Continue to deal" from an exclusive hold (ID-119): the server snapshots price and commission from the hold.
  exclusiveHoldId?: string;
  catalogueProjectId?: number;
  developerName?: string | null;
  unitNumber?: string | null;
  unitType?: string | null;
  clientName?: string | null;
  clientPhone?: string | null;
  earningsAed?: number | null;
  readOnlyPrice?: true;
};

export type ExclusiveHoldHandOff = {
  exclusiveHoldId: string;
  catalogueProjectId: number | null;
  earningsAed: number | null;
};

export type OnBehalfOfAgent = {
  agentProfileId: string;
  fullName: string;
};

export const useDealRegistrationStore = defineStore('dealRegistration', () => {
  // Token step
  const dealId = ref<string | null>(null);
  const dealNumber = ref<string | null>(null);
  // DB record ID (set after first API save)
  const dealDbId = ref<string | null>(null);
  const tokenAmount = ref<string | null>(null);
  const tokenCurrency = ref<string>('AED');
  const tokenDate = ref<string | null>(null);

  // #40 — Pre-population from "Register this unit" button on project detail.
  // Consumed by deals-register.vue on mount, then cleared.
  const prePopulation = ref<DealPrePopulation | null>(null);

  // AM submitting on behalf of one of their assigned agents.
  // Set from /am/agents CTA, consumed by the deal-create POST, cleared on flow exit.
  const onBehalfOfAgent = ref<OnBehalfOfAgent | null>(null);

  // Portal project id from "Register this unit"; the server decides whether it is an exclusive (ID-133).
  const projectId = ref<string | null>(null);
  // Hold carried into this registration (ID-119): sent as exclusiveHoldId on the create POST; unit + price read-only.
  const exclusiveHold = ref<ExclusiveHoldHandOff | null>(null);

  // Booking form step
  const projectName = ref<string | null>(null);
  const developer = ref<string | null>(null);
  const unitNumber = ref<string | null>(null);
  const unitType = ref<string | null>(null);
  const salePrice = ref<string | null>(null);
  const estimatedCommission = ref<string | null>(null);
  const devCommissionRate = ref<number>(5);
  const agentSplitPercent = ref<number>(50);

  // Deal path (self-close or consultant)
  const dealPath = ref<'self-close' | 'consultant' | null>(null);

  // Consultant path fields
  const clientFirstName = ref<string | null>(null);
  const clientNationality = ref<string | null>(null);
  const clientLanguage = ref<string | null>(null);
  const clientBudget = ref<string | null>(null);

  // Self-close client details (register-token step)
  const clientName = ref<string | null>(null);
  const clientPhone = ref<string | null>(null);
  const clientEmail = ref<string | null>(null);
  const clientAddress = ref<string | null>(null);

  // SPA step
  const spaConfirmedAt = ref<string | null>(null);
  const status = ref<string | null>(null);
  // Payout option picked on the SPA step (ID-115); the server stays the source of truth.
  const payoutOption = ref<'STANDARD' | 'FASTPAY' | null>(null);

  // Bank details
  const bankDetailsSaved = ref(false);

  // Jump-to-stage (agent skipped earlier steps)
  const jumpedToStage = ref<number | null>(null);

  const setTokenDeal = (data: {
    dealId: string;
    dealNumber: string;
    tokenAmount: string;
    tokenCurrency: string;
    tokenDate: string;
  }) => {
    dealId.value = data.dealId;
    dealNumber.value = data.dealNumber;
    tokenAmount.value = data.tokenAmount;
    tokenCurrency.value = data.tokenCurrency;
    tokenDate.value = data.tokenDate;
  };

  const setBookingDeal = (data: {
    projectName: string;
    developer: string;
    unitNumber: string;
    unitType: string;
    salePrice: string;
    estimatedCommission: string;
    devCommissionRate: number;
    agentSplitPercent: number;
  }) => {
    projectName.value = data.projectName;
    developer.value = data.developer;
    unitNumber.value = data.unitNumber;
    unitType.value = data.unitType;
    salePrice.value = data.salePrice;
    estimatedCommission.value = data.estimatedCommission;
    devCommissionRate.value = data.devCommissionRate;
    agentSplitPercent.value = data.agentSplitPercent;
  };

  const setPrePopulation = (data: DealPrePopulation) => {
    prePopulation.value = data;
  };

  const clearPrePopulation = () => {
    prePopulation.value = null;
    projectId.value = null;
  };

  const setOnBehalfOfAgent = (data: OnBehalfOfAgent) => {
    onBehalfOfAgent.value = data;
  };

  const clearOnBehalfOfAgent = () => {
    onBehalfOfAgent.value = null;
  };

  const setJumpedToStage = (stage: number) => {
    jumpedToStage.value = stage;
    // Auto-generate a deal number so downstream pages always have one
    if (!dealNumber.value) {
      dealNumber.value = 'DIC-' + Math.floor(10000 + Math.random() * 90000);
    }
  };

  const setConsultantDeal = (data: {
    dealNumber: string;
    clientFirstName: string;
    clientNationality: string;
    clientLanguage: string;
    clientBudget: string;
    projectName: string;
  }) => {
    dealNumber.value = data.dealNumber;
    clientFirstName.value = data.clientFirstName;
    clientNationality.value = data.clientNationality;
    clientLanguage.value = data.clientLanguage;
    clientBudget.value = data.clientBudget;
    projectName.value = data.projectName;
    dealPath.value = 'consultant';
  };

  const confirmSpa = () => {
    spaConfirmedAt.value = new Date().toISOString();
    status.value = 'SPA_PENDING_VERIFICATION';
  };

  // Hydrate the registration store from an existing deal record (the row
  // shape returned by /api/agent/deals). Used when the agent re-enters the
  // wizard from /deals to "resume" / add to an in-progress deal — without
  // this, every PATCH would silently land as a fresh POST.
  const loadFromExistingDeal = (deal: {
    id: string;
    dealNumber?: string | null;
    projectName?: string | null;
    developer?: string | null;
    unitNumber?: string | null;
    unitType?: string | null;
    salePrice?: number | string | null;
    tokenAmount?: number | string | null;
    tokenCurrency?: string | null;
    tokenDate?: string | null;
    estimatedCommission?: number | string | null;
    path?: 'CONSULTANT' | 'SELF_CLOSE' | string | null;
    spaConfirmedAt?: string | null;
    status?: string | null;
    clientName?: string | null;
    clientPhone?: string | null;
    clientEmail?: string | null;
    clientAddress?: string | null;
  }) => {
    dealDbId.value = deal.id;
    dealNumber.value = deal.dealNumber ?? null;
    projectName.value = deal.projectName ?? null;
    developer.value = deal.developer ?? null;
    unitNumber.value = deal.unitNumber ?? null;
    unitType.value = deal.unitType ?? null;
    salePrice.value = deal.salePrice == null ? null : String(deal.salePrice);
    tokenAmount.value = deal.tokenAmount == null ? null : String(deal.tokenAmount);
    tokenCurrency.value = deal.tokenCurrency ?? 'AED';
    tokenDate.value = deal.tokenDate ?? null;
    estimatedCommission.value = deal.estimatedCommission == null ? null : String(deal.estimatedCommission);
    spaConfirmedAt.value = deal.spaConfirmedAt ?? null;
    status.value = deal.status ?? null;
    clientName.value = deal.clientName ?? null;
    clientPhone.value = deal.clientPhone ?? null;
    clientEmail.value = deal.clientEmail ?? null;
    clientAddress.value = deal.clientAddress ?? null;
    dealPath.value = deal.path === 'CONSULTANT' ? 'consultant' : deal.path === 'SELF_CLOSE' ? 'self-close' : null;
    // Allow the wizard's stage guard to accept arbitrary entry points.
    jumpedToStage.value = jumpedToStage.value ?? 4;
  };

  // Drop any in-progress deal (DB id + wizard fields) while keeping the entry
  // context (AM on-behalf-of agent, "Register this unit" pre-population). Called
  // when the stage picker mounts so a new registration can never PATCH the
  // previous deal still held in memory.
  const resetProgress = () => {
    const keepOnBehalfOf = onBehalfOfAgent.value;
    clear();
    onBehalfOfAgent.value = keepOnBehalfOf;
  };

  const clear = () => {
    jumpedToStage.value = null;
    dealId.value = null;
    dealNumber.value = null;
    dealDbId.value = null;
    tokenAmount.value = null;
    tokenCurrency.value = 'AED';
    tokenDate.value = null;
    projectId.value = null;
    projectName.value = null;
    developer.value = null;
    unitNumber.value = null;
    unitType.value = null;
    salePrice.value = null;
    estimatedCommission.value = null;
    devCommissionRate.value = 5;
    agentSplitPercent.value = 50;
    spaConfirmedAt.value = null;
    status.value = null;
    payoutOption.value = null;
    dealPath.value = null;
    clientFirstName.value = null;
    clientNationality.value = null;
    clientLanguage.value = null;
    clientBudget.value = null;
    clientName.value = null;
    clientPhone.value = null;
    clientEmail.value = null;
    clientAddress.value = null;
    onBehalfOfAgent.value = null;
    exclusiveHold.value = null;
  };

  return {
    dealId,
    dealNumber,
    dealDbId,
    tokenAmount,
    tokenCurrency,
    tokenDate,
    projectId,
    projectName,
    developer,
    unitNumber,
    unitType,
    salePrice,
    estimatedCommission,
    devCommissionRate,
    agentSplitPercent,
    spaConfirmedAt,
    status,
    payoutOption,
    bankDetailsSaved,
    jumpedToStage,
    dealPath,
    clientFirstName,
    clientNationality,
    clientLanguage,
    clientBudget,
    clientName,
    clientPhone,
    clientEmail,
    clientAddress,
    prePopulation,
    onBehalfOfAgent,
    exclusiveHold,
    setTokenDeal,
    setBookingDeal,
    confirmSpa,
    setJumpedToStage,
    setConsultantDeal,
    clear,
    resetProgress,
    setPrePopulation,
    clearPrePopulation,
    setOnBehalfOfAgent,
    clearOnBehalfOfAgent,
    loadFromExistingDeal,
  };
});
